/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Table, Popup, Label, Icon, Loader } from 'semantic-ui-react';
import { savePDF } from '@progress/kendo-react-pdf';
import capitalize from 'utils/capitalize';
import SimpleModal from 'components/common/modals/SimpleModal';
import Receipt from 'components/Wallet/Receipt';
import Request from 'components/Wallet/Request';
import formatNumber from 'helpers/formatNumber';
import { isFinanceAdmin } from 'helpers/checkRoles';
import CreditArrow from 'assets/images/credit-wallet-arrow.svg';
import DebitArrow from 'assets/images/debit-wallet-arrow.svg';
import { getTransactionTitle } from 'constants/transactionPurposes';
import {
  GROUP_WALLET,
  INDIVIDUAL_WALLET,
} from 'constants/walletTransactionsContext';
import Withdrawal from '../Withdrawal';
import TransactionPopupContent from './PopupContent';
import TransactionDetailsPopup from './TransactionDetailsPopup';
import './index.scss';

const statusColors = {
  approved: '#48ff5b',
  declined: '#ff4869',
  pending: '#aa5E00',
  success: '#029B5d',
  failed: '#f04043',
  rejected: '#ff4869',
};

const typeColors = {
  credit: '#00A562',
  debit: '#2D62DD',
};

const getBalance = (
  {
    userBalanceAfter,
    userBalanceBefore,
    groupBalanceAfter,
    groupBalanceBefore,
    orgBalanceAfter,
    orgBalanceBefore,
  },
  context,
  isSuccess,
) => {
  switch (context) {
    case INDIVIDUAL_WALLET:
      return isSuccess ? userBalanceAfter : userBalanceBefore;
    case GROUP_WALLET:
      return isSuccess ? groupBalanceAfter : groupBalanceBefore;
    default:
      return isSuccess ? orgBalanceAfter : orgBalanceBefore;
  }
};

const Transactions = ({
  items = [],
  roles,
  viewAll,
  context,
  showGroupDetails,
  loading,
  onTransactionRefresh,
}) => {
  const [title, setTitle] = useState('Transaction');
  const [showReceipt, setShowReceipt] = useState(false);
  const [receipt, setReceipt] = useState({});
  const [downloadReceipt, setDownloadReceipt] = useState();
  const [orgRequestPending, setOrgRequestPending] = useState(
    'transaction',
  );

  const handleSetReceipt = data => {
    setReceipt(data);
  };

  const handleDownloadReceipt = data => {
    setReceipt(data);
    setDownloadReceipt(true);
  };

  useEffect(() => {
    if (downloadReceipt) {
      const receiptElement = document.getElementById('receipt');
      savePDF(
        receiptElement,
        {
          paperSize: 'auto',
          margin: 40,
          fileName: 'Receipt',
        },
        () =>
          setTimeout(() => {
            setShowReceipt(false);
          }, 1000),
      );
    }

    setDownloadReceipt(false);
  }, [downloadReceipt]);

  const popupContentItems = {
    default: {
      items: [
        {
          label: ' View transaction receipt',
          onClick: currentReceipt => {
            setShowReceipt(true);
            handleSetReceipt(currentReceipt);
            setTitle('Transaction');
          },
        },
        {
          label: ' Download receipt PDF',
          onClick: currentReceipt => {
            setShowReceipt(true);
            handleDownloadReceipt(currentReceipt);
            setTitle('Transaction');
          },
        },
      ],
    },
    org_wallet_topup: {
      items: [
        {
          label: 'View Request Details',
          onClick: currentReceipt => {
            setShowReceipt(true);
            handleSetReceipt(currentReceipt);
            setOrgRequestPending('topup');
            setTitle('Transaction');
          },
        },
      ],
    },
    org_wallet_view_withdrawal: {
      items: [
        {
          label: 'View transaction',
          onClick: currentReceipt => {
            setShowReceipt(true);
            handleSetReceipt(currentReceipt);
            setOrgRequestPending('withdrawal');
            setTitle('Withdrawal Transaction');
          },
        },
      ],
    },
    org_wallet_process_withdrawal: {
      items: [
        {
          label: 'Process transaction',
          onClick: currentReceipt => {
            setShowReceipt(true);
            handleSetReceipt(currentReceipt);
            setOrgRequestPending('withdrawal');
            setTitle('Process Withdrawal');
          },
        },
      ],
    },
  };

  const getContentItem = ({ status, type, transactionPurpose }) => {
    if (status === 'pending') {
      if (
        type === 'org_wallet_topup' ||
        transactionPurpose === 'org_wallet_topup'
      )
        return popupContentItems.org_wallet_topup;

      if (
        transactionPurpose === 'org_wallet_withdrawal' &&
        isFinanceAdmin(roles)
      )
        return popupContentItems.org_wallet_process_withdrawal;
    }

    if (transactionPurpose === 'org_wallet_withdrawal')
      return popupContentItems.org_wallet_view_withdrawal;

    return popupContentItems.default;
  };

  if (loading) return <Loader active inline="centered" />;

  return (
    <>
      <div className="wallet__transactions">
        <Table basic="very" size="large">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width="1" textAlign="center">
                Type
              </Table.HeaderCell>
              <Table.HeaderCell>Purpose</Table.HeaderCell>
              <Table.HeaderCell>Provider</Table.HeaderCell>
              <Table.HeaderCell>Amount (RWF)</Table.HeaderCell>
              <Table.HeaderCell>Balance (RWF)</Table.HeaderCell>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell width="1" />
            </Table.Row>
          </Table.Header>

          <Table.Body
            className={`wallet__transactions--table-body ${viewAll &&
              'lg-tbody'}`}
          >
            {(items || [])
              .sort(
                (a, b) =>
                  new Date(b.created_at) - new Date(a.created_at),
              )
              .map(
                ({
                  id,
                  type,
                  phone_number: phoneNumber,
                  created_at: date,
                  amount,
                  comment,
                  provider_name: channel,
                  purpose: transactionPurpose,
                  status,
                  description,
                  org_wallet_id,
                  attachment,
                  user_id: userId,
                  group_id: groupId,
                  bank_ref_id: providerTid,
                  recipient_bank_account: recipientBankAccount,
                  source,
                  destination,
                  from,
                  group_name: groupName,
                  org_name: orgName,
                  first_name: firstName,
                  last_name: lastName,
                  wallet_organization_balance_after: orgBalanceAfter,
                  wallet_organization_balance_before: orgBalanceBefore,
                  group_balance_after: groupBalanceAfter,
                  group_balance_before: groupBalanceBefore,
                  user_wallet_balance_after: userBalanceAfter,
                  user_wallet_balance_before: userBalanceBefore,
                  response_error_message: responseErrorMessage,
                }) => {
                  const sourceLabels = {
                    save_wallet_account: orgName || 'Save',
                    individual_wallet: firstName
                      ? `${firstName} ${lastName}`
                      : phoneNumber,
                    group_wallet: groupName,
                    micro_loan_fund: 'Micro Loan Fund',
                  };
                  const destLabels = {
                    group_wallet: groupName,
                    individual_wallet: firstName
                      ? `${firstName} ${lastName}`
                      : phoneNumber,
                    telco: phoneNumber,
                    subscription_fees_account: 'Subscription Fees',
                    save_float: 'Save Float',
                  };

                  const originator =
                    transactionPurpose === 'org_loan'
                      ? from
                      : sourceLabels[source] ??
                        capitalize(source?.replace(/_/g, ' ') || '-');

                  const formatedRecipient =
                    destLabels[destination] ??
                    capitalize(
                      destination?.replace(/_/g, ' ') || '-',
                    );

                  const currentReceipt = {
                    originator,
                    recipient: formatedRecipient,
                    transactionId: id,
                    date,
                    transactionPurpose,
                    channel,
                    amount,
                    comment,
                    org_wallet_id,
                    roles,
                    attachment,
                    status,
                    description,
                    type,
                    recipientBankAccount,
                  };

                  return (
                    <Table.Row key={id}>
                      <Table.Cell
                        width="1"
                        textAlign="center"
                        verticalAlign="middle"
                        className="wallet__transactions--type"
                      >
                        <Label
                          circular
                          size="large"
                          style={{
                            backgroundColor: `${typeColors[type]}20`,
                          }}
                        >
                          <img
                            alt={type}
                            style={{ display: 'block' }}
                            className="wallet__transactions--type--img"
                            src={
                              type === 'credit'
                                ? CreditArrow
                                : DebitArrow
                            }
                          />
                        </Label>
                      </Table.Cell>
                      <Table.Cell className="wallet__transactions--purpose">
                        <div className="wallet__transactions--title">
                          {getTransactionTitle(transactionPurpose)}
                        </div>
                        {type === 'credit' ||
                        transactionPurpose === 'org_loan' ? (
                          <div className="wallet__transactions--from">
                            <Icon name="arrow left" />
                            From: {originator}
                          </div>
                        ) : (
                          <div className="wallet__transactions--to">
                            <Icon name="arrow right" />
                            To: {formatedRecipient}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell className="wallet__transactions--provider">
                        <Icon name="building outline" />
                        {String(channel).replace(/_/g, ' ')}
                      </Table.Cell>
                      <Table.Cell className="wallet__transactions--amount">
                        <Icon name="money bill alternate outline" />
                        {formatNumber(amount)}
                      </Table.Cell>
                      <Table.Cell className="wallet__transactions--balance">
                        <Icon name="briefcase" />
                        {formatNumber(
                          getBalance(
                            {
                              userBalanceAfter,
                              userBalanceBefore,
                              groupBalanceAfter,
                              groupBalanceBefore,
                              orgBalanceAfter,
                              orgBalanceBefore,
                            },
                            context,
                            status === 'success',
                          ),
                        )}
                      </Table.Cell>
                      <Table.Cell className="wallet__transactions--date">
                        <Icon name="calendar alternate outline" />
                        {new Date(date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </Table.Cell>
                      <Table.Cell className="wallet__transactions--status">
                        <Label
                          size="tiny"
                          style={{
                            color: statusColors[status.trim()],
                            backgroundColor: `${
                              statusColors[status.trim()]
                            }20`,
                          }}
                        >
                          {status.trim().toUpperCase()}
                        </Label>
                      </Table.Cell>
                      <Table.Cell width="1">
                        <div className="wallet__transactions--actions">
                          {showGroupDetails ? (
                            <Popup
                              key={`details-${id}`}
                              basic
                              hoverable
                              on="click"
                              position="left center"
                              trigger={
                                <Icon
                                  link
                                  name="info circle"
                                  title="Transaction details"
                                />
                              }
                              content={
                                <TransactionDetailsPopup
                                  transaction={{
                                    id,
                                    type,
                                    userId,
                                    groupId,
                                    providerTid,
                                    status,
                                    comment,
                                    responseErrorMessage,
                                  }}
                                  onTransactionRefresh={
                                    onTransactionRefresh
                                  }
                                />
                              }
                            />
                          ) : (
                            <Popup
                              key={`details-${id}`}
                              basic
                              hoverable
                              on="click"
                              position="left center"
                              trigger={
                                <Icon
                                  link
                                  name="info circle"
                                  title="Transaction details"
                                />
                              }
                              content={
                                <TransactionDetailsPopup
                                  transaction={{
                                    id,
                                    type,
                                    userId,
                                    groupId,
                                    providerTid,
                                    status,
                                    comment,
                                    responseErrorMessage,
                                  }}
                                  onTransactionRefresh={
                                    onTransactionRefresh
                                  }
                                />
                              }
                            />
                          )}

                          <Popup
                            key={id}
                            basic
                            hoverable
                            position="left center"
                            on="click"
                            pinned
                            content={
                              <TransactionPopupContent
                                items={getContentItem({
                                  status,
                                  type,
                                  transactionPurpose,
                                })}
                                data={currentReceipt}
                              />
                            }
                            trigger={
                              <Icon
                                link
                                name="file alternate outline"
                                title="View receipt"
                              />
                            }
                          />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                },
              )}
          </Table.Body>
        </Table>
      </div>

      <SimpleModal
        title={title}
        open={showReceipt}
        onClose={() => {
          setShowReceipt(false);
          setOrgRequestPending(false);
        }}
        contentStyle={{ padding: '0px' }}
      >
        {orgRequestPending === 'topup' ? (
          <Request {...receipt} setShowReceipt={setShowReceipt} />
        ) : orgRequestPending === 'withdrawal' ? (
          <Withdrawal {...receipt} setShowReceipt={setShowReceipt} />
        ) : (
          <Receipt {...receipt} />
        )}
      </SimpleModal>
    </>
  );
};

Transactions.defaultProps = {
  viewAll: false,
  roles: [],
  context: null,
  showGroupDetails: false,
  loading: false,
  onTransactionRefresh: null,
};

Transactions.propTypes = {
  items: PropTypes.instanceOf(Array).isRequired,
  viewAll: PropTypes.bool,
  roles: PropTypes.instanceOf(Array),
  context: PropTypes.string,
  showGroupDetails: PropTypes.bool,
  loading: PropTypes.bool,
  onTransactionRefresh: PropTypes.func,
};

export default Transactions;
